import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ChevronRight, Layers3, Wrench, Cpu, Box } from "lucide-react";
import { CAD_SERVICES } from "@/data/cadServices";
import ContourBackground from "@/components/ContourBackground";
import GridFillerCards from "@/components/GridFillerCards";
import SubServiceFillerCell from "@/components/SubServiceFillerCell";
import { padForGrid } from "@/lib/gridUtils";
import useSeo from "@/hooks/useSeo";

const TECH_STACK = [
  { t: "AutoCAD / Revit / Inventor", d: "Autodesk full-stack drafting and BIM modeling." },
  { t: "SolidWorks / Creo / NX", d: "Mechanical 3D parametric modeling and product design." },
  { t: "Tekla Structures", d: "Steel and concrete structural detailing." },
  { t: "MicroStation / Bentley", d: "Bentley-stack drafting for infrastructure and plant projects." },
  { t: "AVEVA / SP3D", d: "Plant design, P&ID, piping and instrumentation." },
  { t: "Navisworks / BIM 360", d: "Clash detection, federated coordination and 4D scheduling." },
];

const ROLES = [
  { icon: <Layers3 className="h-5 w-5" />, t: "Architectural Drafter", d: "Permit sets, floor plans, elevations and millwork shop drawings." },
  { icon: <Wrench className="h-5 w-5" />, t: "Structural Detailer", d: "Steel/rebar/precast shop and erection drawings to AISC, ACI and Eurocode." },
  { icon: <Cpu className="h-5 w-5" />, t: "Mechanical Modeler", d: "SolidWorks/Inventor product design, sheet-metal and assemblies." },
  { icon: <Box className="h-5 w-5" />, t: "MEP / BIM Coordinator", d: "Revit MEP, clash detection and federated model coordination." },
];

export default function CadTechies() {
  useSeo({
    title: "CAD Techies — Architectural · Structural · MEP · Mechanical · Civil · Tri-Axis XYZ",
    description:
      "Outsourced CAD drafting and BIM services for architecture, structural, MEP, mechanical, electrical, civil-engineering, 3D rendering, PDF-to-CAD and solar PV teams.",
  });

  return (
    <div data-testid="page-cad-techies">
      <Hero />
      <CategoryGrid />
      <Roles />
      <TechStack />
      <DetailsSections />
    </div>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-slate-200">
      <ContourBackground variant="light" opacity={0.5} seed={7} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
            CAD Techies · Beyond Land Survey
          </div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
            Outsourced CAD for{" "}
            <span className="text-emerald-700">architecture, structural, MEP</span> &{" "}
            <span className="text-orange-survey">mechanical</span> teams.
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            12 service lines covering architectural drafting, structural detailing, MEP
            & BIM coordination, mechanical and electrical drafting, civil engineering,
            3D rendering, PDF-to-CAD conversion, solar PV and signage. Embed our techies
            into your design pipeline by the hour, day or project.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="cad-techies-cta-hire"
              className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-5 py-3"
            >
              Hire a CAD Techie <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/survey-techies"
              data-testid="cad-techies-cta-survey"
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-5 py-3 bg-white"
            >
              Land-Survey Drafting → <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="corner-marks text-slate-900 border border-slate-200 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1581094271901-8022df4466f9?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="CAD engineering drawings on screen"
              className="w-full h-[420px] object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Category Grid ---------- */
function CategoryGrid() {
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              01 · CAD Service Lines
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
              12 disciplines.{" "}
              <span className="text-emerald-700">One trusted partner.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-slate-600">
            Click any card to jump to the discipline below and see the sub-services we
            support.
          </div>
        </div>

        <div data-reveal="up" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {CAD_SERVICES.map((s, i) => (
            <a
              key={s.slug}
              href={`#cad-${s.slug}`}
              data-testid={`cad-services-card-${s.slug}`}
              className="tri-card group bg-white p-6 lg:p-7 flex flex-col gap-4 min-h-[220px] relative"
            >
              <div className="flex items-center justify-between">
                <span className="text-3xl leading-none">{s.emoji}</span>
                <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div>
                <div className="display text-lg sm:text-xl font-extrabold tracking-tight text-slate-900">
                  {s.name}
                </div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {s.short}
                </div>
              </div>
              <div className="mt-auto flex items-center gap-1 text-sm font-semibold text-topo">
                View sub-services{" "}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>
          ))}
          <GridFillerCards
            count={padForGrid(CAD_SERVICES.length)}
            kind="cad"
            startIndex={CAD_SERVICES.length}
          />
        </div>
      </div>
    </section>
  );
}

/* ---------- Roles ---------- */
function Roles() {
  return (
    <section className="border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
          02 · Talent roles
        </div>
        <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 max-w-3xl">
          Roles you can <span className="text-emerald-700">embed today</span>.
        </h2>
        <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {ROLES.map((r) => (
            <li
              key={r.t}
              data-testid={`cad-role-${r.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="bg-white p-6 lg:p-7 tri-card"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center bg-orange-survey text-white rounded-sm">
                {r.icon}
              </span>
              <div className="display mt-4 text-lg font-extrabold tracking-tight text-slate-900">
                {r.t}
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.d}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------- Tech Stack ---------- */
function TechStack() {
  return (
    <section className="border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              03 · CAD Tech stack
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
              Fluent in every major{" "}
              <span className="text-emerald-700">design & detailing</span> platform.
            </h2>
          </div>
          <div className="lg:col-span-5 text-slate-600">
            Tell us your platform, your version and your template — we&apos;ll be on it
            within 24 hours.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {TECH_STACK.map((s, i) => (
            <div key={s.t} className="bg-white p-6 tri-card">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="display mt-2 text-lg font-extrabold tracking-tight text-slate-900">
                {s.t}
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Per-Discipline Detail Sections ---------- */
function DetailsSections() {
  return (
    <section className="border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo mb-6">
          04 · Discipline detail
        </div>
        <div className="space-y-px bg-slate-200 border border-slate-200">
          {CAD_SERVICES.map((s, idx) => (
            <article
              key={s.slug}
              id={`cad-${s.slug}`}
              data-testid={`cad-detail-${s.slug}`}
              data-reveal="up"
              className="bg-white p-6 lg:p-10 scroll-mt-24"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl leading-none">{s.emoji}</span>
                    <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-400">
                      {String(idx + 1).padStart(2, "0")} · {s.name}
                    </span>
                  </div>
                  <h3 className="display mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.hero}</p>
                  <div className="mt-5">
                    <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                      What we deliver
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {s.deliverables.map((d) => (
                        <li
                          key={d}
                          className="flex items-start gap-2 text-sm text-slate-700"
                        >
                          <ChevronRight className="h-4 w-4 mt-0.5 text-orange-survey shrink-0" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link
                    to="/contact"
                    data-testid={`cad-detail-cta-${s.slug}`}
                    className="mt-6 inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2.5"
                  >
                    Request {s.name} quote <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>

                <div className="lg:col-span-7">
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                    Sub-services
                  </div>
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                    {s.subpages.map((sp) => (
                      <li
                        key={sp.slug}
                        data-testid={`cad-subpage-${s.slug}-${sp.slug}`}
                        className="bg-white p-4 lg:p-5"
                      >
                        <div className="display text-sm font-extrabold tracking-tight text-slate-900">
                          {sp.name}
                        </div>
                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                          {sp.body}
                        </p>
                      </li>
                    ))}
                    {padForGrid(s.subpages.length, 2) === 1 && (
                      <SubServiceFillerCell
                        serviceName={s.name}
                        testid={`cad-subpage-filler-${s.slug}`}
                      />
                    )}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
